import { FormControl, InputAdornment, MenuItem, Select } from '@mui/material'
import { connectField } from 'dynamic-uniforms'
import { Markdown } from '../../Markdown'
import { useCiboAutoForm } from './CiboAutoFormContext'
import { ClearButton } from './ClearButton'
import { FieldLabel } from './FieldLabel'
import { filterDOMProps } from './filterDOMProps'
import { ValidationMessages } from './ValidationMessages'

interface SelectFieldProps {
  value: string | undefined | null
  onChange: (value: string | undefined) => void
  label?: string
  disabled?: boolean
  error?: any
  helperText?: string
  required?: boolean
  name: string
  placeholder?: string
  [key: string]: any // uniforms injects many props
}

function SelectField({
  value,
  onChange,
  label,
  error,
  helperText,
  disabled,
  required,
  placeholder,
  description,
  ...props
}: SelectFieldProps) {
  const { touch } = useCiboAutoForm()
  const canClear = !disabled && value != null && value !== ''

  const options = (props.oneOf || props.options || []).map((option: any) =>
    typeof option === 'object'
      ? {
          label: option.label ?? option.title ?? option.const ?? option.value,
          value: option.const ?? option.value,
        }
      : { value: option, label: option }
  )

  return (
    <FormControl fullWidth {...filterDOMProps(props)} required={required} disabled={disabled}>
      {label && <FieldLabel label={label} required={required} />}

      <Select
        data-testid={`select-field-${props.name}`}
        {...filterDOMProps(props)}
        value={value ?? ''}
        required={!!required}
        disabled={disabled}
        endAdornment={
          canClear ? (
            <InputAdornment position="end" sx={{ mr: 3 }}>
              <ClearButton
                tooltipKey="clearButton.value"
                onClick={() => {
                  onChange(undefined)
                  touch(props.name)
                }}
              />
            </InputAdornment>
          ) : undefined
        }
        onChange={e => {
          const v = e.target.value
          // Empty string → undefined (Uniforms best practice)
          if (v === '') onChange(undefined)
          else onChange(v as string)

          touch(props.name)
        }}
        onBlur={() => touch(props.name)}
      >
        {options.map((option: any) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label ?? option.value}
          </MenuItem>
        ))}
      </Select>

      <ValidationMessages fieldName={props.name} error={error} />

      {!!description && <Markdown typographyProps={{ variant: 'caption' }}>{description}</Markdown>}
    </FormControl>
  )
}

export default connectField(SelectField)
